import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, mergeMap, switchMap } from "rxjs/operators";
import { of } from "rxjs";
import { AdministrationService } from "../../service/administration.service";
import { UsersActions } from "./users.feature";
import { AdministrationActions } from "../administration/administration.actions";
import { UserService } from "../../service/user.service";
import { DownloadUserQueryResponse } from "../../../../graphql/generated";
import { Users } from "../../shared/models/user.model";
import { LegalEntity } from "../../shared/models/legal-entity.model";
import { GraphQLResponse } from "../../shared/models/grapql.models";

export type GraphResponse<T> = {
  data: {
    [id: string]: T[];
  };
};

@Injectable()
export class UserEffects {
  loadUsers$ = createEffect(
    (usService: UserService = inject(UserService)) => {
      return this.actions$.pipe(
        ofType(UsersActions.loadUsers),
        switchMap(({ parameters, fields }) =>
          usService.getUsers(parameters).pipe(
            map((users) =>
              UsersActions.loadUsersSuccess({
                users: {
                  ...users,
                  data: (users?.data ?? []) as unknown as Users[]
                } as GraphQLResponse<Users>,
                fields: fields
              })
            ),
            catchError((error) => of(UsersActions.loadUsersFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  /** The legal entities of a user are loaded only when its table row gets expanded */
  loadUserLegalEntities$ = createEffect(
    (usService: UserService = inject(UserService)) => {
      return this.actions$.pipe(
        ofType(UsersActions.loadUserLegalEntities),
        mergeMap(({ userId }) =>
          usService.getUserLegalEntities(userId).pipe(
            map((response) =>
              UsersActions.loadUserLegalEntitiesSuccess({
                userId,
                legalEntity: (response?.data ?? []) as unknown as LegalEntity[]
              })
            ),
            catchError((error) => of(UsersActions.loadUserLegalEntitiesFailure({ userId, error })))
          )
        )
      );
    },
    { functional: true }
  );


  changeUserPermission$ = createEffect(
    (usService: UserService = inject(UserService)) => {
      return this.actions$.pipe(
        ofType(UsersActions.changeUserPermission),
        mergeMap(({ legalEntity, user }) =>
          usService.updateUserPermission(String(user.id), legalEntity.legalEntityTrackingId!, legalEntity.permissionLevel!).pipe(
            map(() => {
              return UsersActions.changeUserPermissionSuccess({ legalEntity, user });
            }),
            catchError((error) => of(UsersActions.changeUserPermissionFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );
  setRole$ = createEffect(
    (usService: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(UsersActions.setRoles),
        mergeMap(() =>
          usService.setRoles().pipe(
            map((roles) => {
              return UsersActions.setRolesSuccess({ roles: roles.data?.[0] ?? '' });
            }),
            catchError((error) => of(UsersActions.setRolesFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  setRoleLabel$ = createEffect(
    (usService: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(UsersActions.setRoles),
        mergeMap(() =>
          usService.setRoles().pipe(
            map((roles) => {
              // Estrai il secondo elemento dell'array, che è il label "umano" del ruolo
              const roleLabel = roles.data?.[1] ?? '';
              return UsersActions.setRoleLabelSuccess({ roleLabel });
            }),
            catchError((error) => of(UsersActions.setRoleLabelFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );
  setRoleAuditSelf$ = createEffect(
    (usService: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(UsersActions.setRolesAuditSelf),
        mergeMap(() =>
          usService.setAuditSelfRoles().pipe(
            map((roles) => {
              return UsersActions.setRolesAuditSelfSuccess({ rolesAuditSelf: roles.data?.[0] ?? '' });
            }),
            catchError((error) => of(UsersActions.setRolesAuditSelfFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  setRolePlannerTool$ = createEffect(
    (usService: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(UsersActions.setRolesPlannerTool),
        mergeMap(() =>
          usService.setPlannerToolRole().pipe(
            map((role) => {
              const rolesPlannerTool = role?.athenaRole ?? '';
              return UsersActions.setRolesPlannerToolSuccess({ rolesPlannerTool });
            }),
            catchError((error) => of(UsersActions.setRolesPlannerToolFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );



  exportUsers$ = createEffect(
    (usService: UserService = inject(UserService), as: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(UsersActions.exportUsers),
        switchMap(({ parameters, timeZone }) =>
          usService.exportUsers(parameters, timeZone).pipe(
            map((users) => {
              as.downloadFile(
                users!.data![0]!.content,
                users!.data![0]!.filename!
              );
              return UsersActions.exportUsersSuccess({
                users: users?.data as DownloadUserQueryResponse
              });
            }),
            catchError((error) =>
              of(UsersActions.exportUsersFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  loadUsersFiltersNames$ = createEffect(
    (usService: UserService = inject(UserService)) => {
      return this.actions$.pipe(
        ofType(AdministrationActions.loadFiltersNames, UsersActions.loadUsersNames),
        switchMap(() =>
          usService.getUsersFilterNames().pipe(
            map((users) =>
              UsersActions.loadUsersNamesSuccess({
                users: {
                  ...users,
                  data: (users?.data ?? []) as unknown as Users[]
                } as GraphQLResponse<Users>
              })
            ),
            catchError((error) =>
              of(UsersActions.loadUsersNamesFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );
  constructor(private actions$: Actions) { }
}
